import { Subject, fromEvent } from 'rxjs';
import * as i0 from '@angular/core';
import { Injectable, HostListener, Directive, Input, HostBinding, NgModule } from '@angular/core';
import { __awaiter } from 'tslib';
import { first, map } from 'rxjs/operators';
import * as i1 from '@one/angular-kit/modal';
import { CommonModule } from '@angular/common';

/*
 * Extract a touched changed observable from an abstract control
 * @param control AbstractControl like object with markAsTouched method
 */
const extractTouchedChanges = (control) => {
    const prevMarkAsTouched = control.markAsTouched;
    const prevMarkAsUntouched = control.markAsUntouched;
    const touchedChanges$ = new Subject();
    function nextMarkAsTouched(...args) {
        touchedChanges$.next(true);
        prevMarkAsTouched.bind(control)(...args);
    }
    function nextMarkAsUntouched(...args) {
        touchedChanges$.next(false);
        prevMarkAsUntouched.bind(control)(...args);
    }
    control.markAsTouched = nextMarkAsTouched;
    control.markAsUntouched = nextMarkAsUntouched;
    return touchedChanges$;
};

class OneCanDeactivateHandling {
    constructor(dialogConfirmation) {
        this.dialogConfirmation = dialogConfirmation;
    }
    openConfirmation(messageData) {
        return __awaiter(this, void 0, void 0, function* () {
            const asyncResult = yield this.openConfirmationDialog(messageData).toPromise();
            return asyncResult;
        });
    }
    openConfirmationDialog(messageData) {
        if (!messageData) {
            messageData = {
                title: 'Confirmation',
                message: 'There are unsaved changes. Clicking Confirm will discard these changes.',
                confirm: 'Confirm',
                cancel: 'Cancel'
            };
        }
        const dialogRef = this.dialogConfirmation.open(messageData.title, messageData.message, messageData.confirm, messageData.cancel);
        return dialogRef.afterClosed().pipe(first(), map((result) => {
            return result;
        }));
    }
}

class OneCanDeactivateService extends OneCanDeactivateHandling {
    constructor(dialogConfirmation) {
        super(dialogConfirmation);
        this.beforeUnloadEvent = fromEvent(window, 'beforeunload').subscribe(event => {
            if (!this.canDeactivate) {
                event.returnValue = true;
            }
        });
    }
    destroySubscriptions() {
        if (this.beforeUnloadEvent) {
            this.beforeUnloadEvent.unsubscribe();
        }
    }
}

class OneCanDeactivateDialogService extends OneCanDeactivateService {
    constructor(dialogConfirmationService) {
        super(dialogConfirmationService);
        this.dialogOpened = false;
    }
    onKeyUp() {
        this.closeDialog();
    }
    closeDialog() {
        if (this.canDeactivate) {
            this.forceCloseDialog();
        }
        else {
            if (!this.dialogOpened) {
                this.dialogOpened = true;
                this.openConfirmation(this.messageData).then((result) => {
                    this.dialogOpened = false;
                    if (result) {
                        this.forceCloseDialog();
                    }
                });
            }
        }
    }
    setMessageData(messageData) {
        this.messageData = messageData;
    }
}
OneCanDeactivateDialogService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneCanDeactivateDialogService, deps: [{ token: i1.OneDialogConfirmationService }], target: i0.ɵɵFactoryTarget.Injectable });
OneCanDeactivateDialogService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneCanDeactivateDialogService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneCanDeactivateDialogService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.OneDialogConfirmationService }]; }, propDecorators: { onKeyUp: [{
                type: HostListener,
                args: ['window:keyup.esc']
            }] } });

class OneCanDeactivateGuard {
    constructor(canDeactivateHandling) {
        this.canDeactivateHandling = canDeactivateHandling;
    }
    canDeactivate(component) {
        return __awaiter(this, void 0, void 0, function* () {
            let result = true;
            if (component.canDeactivate !== undefined && !component.canDeactivate) {
                result = yield this.canDeactivateHandling.openConfirmation(null);
            }
            return result;
        });
    }
}
OneCanDeactivateGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneCanDeactivateGuard, deps: [{ token: OneCanDeactivateHandling }], target: i0.ɵɵFactoryTarget.Injectable });
OneCanDeactivateGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneCanDeactivateGuard });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneCanDeactivateGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: OneCanDeactivateHandling }]; } });

var oneThemeImageType;
(function (oneThemeImageType) {
    oneThemeImageType["dark"] = "dark";
    oneThemeImageType["light"] = "light";
})(oneThemeImageType || (oneThemeImageType = {}));
class OneThemeImageDirective {
    constructor(renderer, el) {
        this.renderer = renderer;
        this.el = el;
        this.oneThemeImageType = oneThemeImageType.light;
    }
    get light() {
        return this.oneThemeImageType === oneThemeImageType.light;
    }
    get dark() {
        return this.oneThemeImageType === oneThemeImageType.dark;
    }
    ngAfterViewInit() {
        this.renderer.addClass(this.el.nativeElement, 'one-theme-image');
    }
}
OneThemeImageDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneThemeImageDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
OneThemeImageDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.5", type: OneThemeImageDirective, selector: "[oneThemeImage]", inputs: { oneThemeImageType: "oneThemeImageType" }, host: { properties: { "class.one-theme-image-light": "this.light", "class.one-theme-image-dark": "this.dark" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneThemeImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[oneThemeImage]'
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { oneThemeImageType: [{
                type: Input
            }], light: [{
                type: HostBinding,
                args: ['class.one-theme-image-light']
            }], dark: [{
                type: HostBinding,
                args: ['class.one-theme-image-dark']
            }] } });

class OneThemeImageModule {
}
OneThemeImageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneThemeImageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
OneThemeImageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneThemeImageModule, declarations: [OneThemeImageDirective], imports: [CommonModule], exports: [OneThemeImageDirective] });
OneThemeImageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneThemeImageModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.5", ngImport: i0, type: OneThemeImageModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [OneThemeImageDirective],
                    exports: [OneThemeImageDirective],
                    imports: [CommonModule]
                }]
        }] });

/*
 * Public API Surface of util
 */

/**
 * Generated bundle index. Do not edit.
 */

export { OneCanDeactivateDialogService, OneCanDeactivateGuard, OneCanDeactivateHandling, OneCanDeactivateService, OneThemeImageDirective, OneThemeImageModule, extractTouchedChanges, oneThemeImageType };
